/**
 * Mobile console overlay for iPad testing
 * Shows console output directly on screen
 */

export interface MobileConsoleAPI {
  readonly log: (...args: any[]) => void;
  readonly clear: () => void;
  readonly show: () => void;
  readonly hide: () => void;
  readonly toggle: () => void;
  readonly destroy: () => void;
}

export const createMobileConsole = (): MobileConsoleAPI => {
  let isDestroyed = false;
  let consoleElement: HTMLElement | null = null;
  let logContainer: HTMLElement | null = null;
  let isVisible = false;

  const init = () => {
    // Create console overlay
    consoleElement = document.createElement("div");
    consoleElement.style.cssText = `
      position: fixed;
      top: 50px;
      right: 10px;
      width: 300px;
      height: 200px;
      background: rgba(0, 0, 0, 0.9);
      color: #00ff00;
      font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
      font-size: 10px;
      line-height: 1.2;
      border: 1px solid #333;
      border-radius: 4px;
      z-index: 10000;
      overflow: hidden;
      display: none;
    `;

    // Header with title and close button
    const header = document.createElement("div");
    header.style.cssText = `
      background: #333;
      color: white;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    const title = document.createElement("span");
    title.textContent = "🖊️ Console";
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.textContent = "×";
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      font-size: 14px;
      cursor: pointer;
      padding: 0 4px;
    `;
    const handleClose = (event: Event) => {
      event.preventDefault();
      hide();
    };
    closeButton.addEventListener("click", handleClose);
    closeButton.addEventListener("touchstart", handleClose, {
      passive: false,
    });
    header.appendChild(title);
    header.appendChild(closeButton);

    // Log container
    logContainer = document.createElement("div");
    logContainer.style.cssText = `
      padding: 8px;
      height: calc(100% - 28px);
      overflow-y: auto;
      white-space: pre-wrap;
      word-break: break-word;
    `;

    consoleElement.appendChild(header);
    consoleElement.appendChild(logContainer);
    document.body.appendChild(consoleElement);

    // Make draggable
    let isDragging = false;
    let dragOffset = { x: 0, y: 0 };

    header.addEventListener("touchstart", (e) => {
      isDragging = true;
      const touch = e.touches[0];
      const rect = consoleElement!.getBoundingClientRect();
      dragOffset.x = touch.clientX - rect.left;
      dragOffset.y = touch.clientY - rect.top;
      e.preventDefault();
    });

    document.addEventListener("touchmove", (e) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      consoleElement!.style.left = touch.clientX - dragOffset.x + "px";
      consoleElement!.style.top = touch.clientY - dragOffset.y + "px";
      consoleElement!.style.right = "auto";
      e.preventDefault();
    });

    document.addEventListener("touchend", () => {
      isDragging = false;
    });
  };

  const log = (...args: any[]) => {
    if (isDestroyed || !logContainer) return;

    const timestamp = new Date().toLocaleTimeString();
    const message = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
      )
      .join(" ");

    const logEntry = document.createElement("div");
    logEntry.style.cssText = `
      margin-bottom: 2px;
      padding: 2px 0;
      border-bottom: 1px solid #222;
    `;
    logEntry.innerHTML = `<span style="color:#666">[${timestamp}]</span> ${message}`;

    logContainer.appendChild(logEntry);
    logContainer.scrollTop = logContainer.scrollHeight;

    // Limit to 50 entries
    while (logContainer.children.length > 50) {
      logContainer.removeChild(logContainer.firstChild!);
    }
  };

  const clear = () => {
    if (logContainer) {
      logContainer.innerHTML = "";
    }
  };

  const show = () => {
    if (consoleElement) {
      consoleElement.style.display = "block";
      isVisible = true;
    }
  };

  const hide = () => {
    if (consoleElement) {
      consoleElement.style.display = "none";
      isVisible = false;
    }
  };

  const toggle = () => {
    if (isVisible) {
      hide();
    } else {
      show();
    }
  };

  const destroy = () => {
    if (consoleElement) {
      document.body.removeChild(consoleElement);
      consoleElement = null;
      logContainer = null;
    }
    isDestroyed = true;
    isVisible = false;
  };

  // Initialize
  init();

  // Intercept console.log
  const originalLog = console.log;
  console.log = (...args) => {
    originalLog(...args);
    log(...args);
  };

  return {
    log,
    clear,
    show,
    hide,
    toggle,
    destroy,
  };
};

// Auto-show console with triple-tap gesture (fingers only, not Apple Pencil)
export const enableTripleTapConsole = (mobileConsole: MobileConsoleAPI) => {
  let tapCount = 0;
  let tapTimer: NodeJS.Timeout;

  document.addEventListener("touchstart", (event: TouchEvent) => {
    // Ignore Apple Pencil touches - only respond to finger touches
    // Apple Pencil has small radiusX (< 10) or touchType === "stylus"
    const touch = event.touches[0];
    if (touch) {
      const isLikelyPencil =
        (touch.radiusX !== undefined && touch.radiusX < 10) ||
        (touch as any).touchType === "stylus";

      if (isLikelyPencil) {
        return; // Don't count pencil touches for triple-tap
      }
    }

    tapCount++;

    if (tapCount === 1) {
      tapTimer = setTimeout(() => {
        tapCount = 0;
      }, 500);
    } else if (tapCount === 3) {
      clearTimeout(tapTimer);
      tapCount = 0;
      mobileConsole.toggle();
    }
  });
};
